import * as Types from '@aeppic/types'
import IChangeReporter from '../shared/types/change-reporter.js'

export enum ChangeType {
  UPDATED = 'updated',
  INSERT_OR_UPDATE = 'insertOrUpdate',
  DELETED = 'deleted',
  UNDELETED = 'undeleted',
  DELETED_HARD = 'deleted-hard',
  MOVED = 'moved',
  CHANGED_FORM = 'changed-form',
  ADDED_DETACHED_VERSION = 'added-detached-version',
}

export interface Change {
  type: ChangeType
  document: Types.Document

  /**
   * Version of the visible model state produced by this change. The sequence
   * is owned by one model instance, resets after boot loading, and is not a
   * durable cursor across server restarts.
   */
  modelVersion?: number

  /** Whether an undelete restores only this document instead of its subtree. */
  isSingleUndelete?: boolean

  /** Recycler folder holding children left behind by a single-document undelete. */
  undeletePlaceholderId?: string
}

export interface ChangeSet {
  changes: Change[]
}

export default class ChangeTracker implements IChangeReporter {
  private _changes: Change[]
  private _modelVersion = 0

  get modelVersion() {
    return this._modelVersion
  }

  resetModelVersion() {
    this._modelVersion = 0
  }

  startTracking() {
    this._changes = [] 
  }

  stopTracking(): Change[] {
    if (this._changes == null) {
      throw new Error('Tracking was not started')
    }

    return this._changes
  }

  private _append(change: Omit<Change, 'modelVersion'>, options: { modelVersion?: number } = {}) {
    if (!this._changes) {
      return
    }

    const modelVersion = options.modelVersion ?? ++this._modelVersion
    const trackedChange: Change = { ...change, modelVersion }
    this._changes.push(trackedChange)
  }

  /**
   * 
   *
   * @param document
   */
  updated(document: Types.Document, { insertOrUpdate = false }) {
    const type = insertOrUpdate === false ? ChangeType.UPDATED : ChangeType.INSERT_OR_UPDATE
    this._append({ type, document })
  }

  /**
   * Document was moved 
   */
  moved(document: Types.Document) {
    this._append({ type: ChangeType.MOVED, document })
  }

  /**
   * Document Form was changed
   */
  changedForm(document: Types.Document, options: { modelVersion?: number } = {}) {
    this._append({ type: ChangeType.CHANGED_FORM, document }, options)
  }
  
  /**
   * Delete of document (moved to the recycler but raises different events and
   * will also mean creating some more documents around it one day) 
   */
  deleted(document: Types.Document) {
    this._append({ type: ChangeType.DELETED, document })
  }

  /**
   * Undelete of document (the document is removed from the recycler and saved with potentially
   * different data than it originally had when deleted) 
   */
  undeleted(document: Types.Document, single = false, placeholderId?: string) {
    this._append({
      type: ChangeType.UNDELETED,
      document,
      isSingleUndelete: single,
      undeletePlaceholderId: placeholderId
    })
  }
  
  /**
   * Document was fully deleted
   */
  deletedHard(document: Types.Document) {
    this._append({ type: ChangeType.DELETED_HARD, document })
  }

  /**
   * A detached Document Version was added
   */
  addedDetachedVersion(document: Types.Document) {
    this._append({ type: ChangeType.ADDED_DETACHED_VERSION, document })
  }
}
